import React, {useEffect, useRef, useState} from "react";
import classNames from "classnames";
import {observer} from "mobx-react-lite";
import intl from "react-intl-universal";
import {useAppState, useUserState} from "@stores/AppStateContext";

import {ModalConfirm} from "../../../components/ModalConfirm";
import {Button} from "../../../components/Button";
import {DateSelect} from "../../../components/form/CalendarSelect/DateSelect";
import {ChevronRightIcon} from "../../../components/icons/ChevronRightIcon";
import {ChevronLeftIcon} from "../../../components/icons/ChevronLeftIcon";
import {ExceptionData, TaskSchedule} from "../../../../stores/taskLists/TaskSchedule";

import {ExceptionsCalendarModalProps, MODALS} from "../modal-names";
import {MONTHS, WEEK_DAYS_SHORT} from "../../../../utils/date";

import {screenSizeProvider} from "../../../../stores/ScreenSizeProvider";

import "./ExceptionsCalendar.css";

export const ExceptionsCalendar = observer((
    {
        schedule,
        initialYear = new Date().getFullYear(),
        onConfirm,
        today = new Date(),
    }: ExceptionsCalendarModalProps
) => {
    const appState = useAppState();
    const userState = useUserState();
    const todayRef = useRef<HTMLDivElement | null>(null);
    const [selectedDateForCalendarSelect, setSelectedDateForCalendarSelect] = useState<Date>(
        new Date(initialYear, 0, 1)
    );
    const selectedYear = selectedDateForCalendarSelect.getFullYear();

    const [localExceptions, setLocalExceptions] = useState<Map<string, ExceptionData>>(new Map(schedule.exceptions));

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            if (todayRef.current) {
                todayRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "center"
                });
            }
        });
        return () => cancelAnimationFrame(id);
    }, []);

    const todayKey = TaskSchedule.getDateKey(today.getFullYear(), today.getMonth(), today.getDate());
    const isMobile = screenSizeProvider.view !== 'desktop';

    const toggleDay = (month: number, day: number) => {
        const key = TaskSchedule.getDateKey(selectedYear, month, day);
        if (key < todayKey) {
            return;
        }
        const map = new Map(localExceptions);
        if (map.has(key)) {
            map.delete(key);
        } else {
            const isWork = schedule.isWorkingByRule(selectedYear, month, day);
            map.set(key, {
                doWeWork: !isWork,
                updatedAt: new Date().toISOString(),
                updatedBy: userState?.currentUserId, // or another identifier
            });
        }
        setLocalExceptions(map);
    };

    const getDaysInMonth = (month: number, year: number) =>
        new Date(year, month + 1, 0).getDate();

    const getWeekDay = (year: number, month: number, day: number) =>
        new Date(year, month, day).getDay(); // 0 — Sunday

    const handleClose = () => {
        appState.setModal(null);
    };

    const handleConfirm = async () => {
        onConfirm(localExceptions);
        handleClose();
    };

    const activeModal = appState.topModal;

    return (
        <ModalConfirm
            contentClassName="ExceptionsCalendarModal"
            maxWidth={1128}
            maxHeight={884}
            header={
                <div className="ExceptionsCalendar__HeaderBlock">
                    <span>{intl.get("ScheduleSetting.overrides").d("Schedule Overrides")}</span>
                    <div className="ExceptionsCalendar__SelectBlock">
                        <Button
                            className="Calendar__Exception__Year__Button"
                            text={intl.get("Calendar-Exception.Month.button").d("Clear all overrides")}
                            type="primary"
                            style="inline"
                            size="md"
                            onClick={() => setLocalExceptions(new Map())}
                        />
                        <div className="TaskLists_calendarSelectBlock">
                            <DateSelect
                                className={"TaskLists_calendarSelect"}
                                placeholder={''}
                                selectedDate={selectedDateForCalendarSelect}
                                onChange={setSelectedDateForCalendarSelect}
                                mode="year"
                                withBackground={true}
                                tooltipTitle={intl.get("CalendarException.selectYear").d("Select a year to edit exceptions for")}
                            />
                            <ChevronLeftIcon
                                className="TaskLists_calendarChevronIcon"
                                additionalPadding={8}
                                onClick={() => {
                                    const prev = new Date(selectedDateForCalendarSelect);
                                    prev.setFullYear(prev.getFullYear() - 1);
                                    setSelectedDateForCalendarSelect(prev);
                                }}
                            />
                            <ChevronRightIcon
                                className="TaskLists_calendarChevronIcon"
                                additionalPadding={8}
                                onClick={() => {
                                    const next = new Date(selectedDateForCalendarSelect);
                                    next.setFullYear(next.getFullYear() + 1);
                                    setSelectedDateForCalendarSelect(next);
                                }}
                            />
                        </div>
                    </div>
                </div>
            }
            open={activeModal === MODALS.EXCEPTIONS_CALENDAR}
            onCancel={handleClose}
            onClose={handleClose}
            onConfirm={handleConfirm}
            isHeaderSticky={true}
        >
            <div className="ExceptionsCalendar__Grid">
                {MONTHS.map((monthName, monthIndex) => {
                    const daysInMonth = getDaysInMonth(monthIndex, selectedYear);

                    return (
                        <div key={monthName} className="ExceptionsCalendar__MonthBlock">
                            <div className="label-large-bold ExceptionsCalendar__MonthName">{monthName}</div>

                            <div className="ExceptionsCalendar__WeekdaysRow">
                                {WEEK_DAYS_SHORT.map((day, i) => (
                                    <div key={i} className="label-small-bold ExceptionsCalendar__WeekDayCell">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            <div className="ExceptionsCalendar__DaysGrid">
                                {Array.from({length: getWeekDay(selectedYear, monthIndex, 1)}, (_, i) => (
                                    <div key={`empty-${i}`} className="ExceptionsCalendar__DayCell"/>
                                ))}

                                {Array.from({length: daysInMonth}, (_, i) => {
                                    const day = i + 1;
                                    const key = TaskSchedule.getDateKey(selectedYear, monthIndex, day);
                                    const exception = localExceptions.get(key)?.doWeWork;
                                    const isScheduleDay = schedule.isWorkingByRule(selectedYear, monthIndex, day);
                                    const classes = {
                                        'ExceptionsCalendar__DayCell': true,
                                        'label-small': !isMobile,
                                        'body-medium': isMobile,
                                        'ExceptionsCalendar__DayCell--disabled': key < todayKey,
                                        'ExceptionsCalendar__DayCell--schedule': isScheduleDay && exception === undefined,
                                        'ExceptionsCalendar__DayCell--work': exception === true,
                                        'ExceptionsCalendar__DayCell--off': exception === false,
                                    };

                                    return (
                                        <div
                                            key={`${monthIndex}-${day}`}
                                            className={classNames(classes)}
                                            onClick={() => toggleDay(monthIndex, day)}
                                            ref={key === todayKey ? todayRef : null}
                                        >
                                            {day}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </ModalConfirm>
    );
});
